'use client';

import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface TabItem {
  label: string;
  content: React.ReactNode;
}

export interface TabsProps {
  items: TabItem[];
  activeTab?: number;
  onTabChange?: (index: number) => void;
  className?: string;
}

export function Tabs({
  items,
  activeTab = 0,
  onTabChange,
  className,
}: TabsProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [showScrollIndicators, setShowScrollIndicators] = useState(false);

  const checkScrollability = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    setShowScrollIndicators(scrollWidth > clientWidth);
  };

  useEffect(() => {
    checkScrollability();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollability);
      window.addEventListener('resize', checkScrollability);
      return () => {
        container.removeEventListener('scroll', checkScrollability);
        window.removeEventListener('resize', checkScrollability);
      };
    }
  }, [items]);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
  };

  return (
    <div className={cn('flex h-full flex-col', className)}>
      <div className="relative border-b border-gray-200 bg-white/50">
        {/* Left scroll indicator */}
        {showScrollIndicators && canScrollLeft && (
          <div className="absolute left-0 top-0 z-10 flex h-full w-8 items-center justify-start bg-gradient-to-r from-white/90 to-transparent">
            <button
              onClick={scrollLeft}
              className="flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm hover:bg-gray-50"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        )}

        {/* Right scroll indicator */}
        {showScrollIndicators && canScrollRight && (
          <div className="absolute right-0 top-0 z-10 flex h-full w-8 items-center justify-end bg-gradient-to-l from-white/90 to-transparent">
            <button
              onClick={scrollRight}
              className="flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm hover:bg-gray-50"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        )}

        <nav
          ref={scrollContainerRef}
          className="scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 -mb-px flex overflow-x-auto px-4 sm:px-6"
          aria-label="Tabs"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {items.map((item, index) => (
            <button
              key={item.label}
              onClick={() => onTabChange?.(index)}
              className={cn(
                'group relative flex-shrink-0 whitespace-nowrap border-b-2 px-3 py-4 text-sm font-medium transition-colors duration-200',
                'mr-6 last:mr-0', // Add margin between tabs, remove from last
                index === activeTab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
              aria-current={index === activeTab ? 'page' : undefined}
            >
              {item.label}
              {index === activeTab && (
                <span className="absolute inset-x-0 -bottom-px h-0.5 bg-gradient-to-r from-primary-500 to-accent-blue" />
              )}
            </button>
          ))}
        </nav>

        {/* Mobile scroll hint - only show on mobile when there are more tabs */}
        {showScrollIndicators && (
          <div className="flex justify-center py-1 sm:hidden">
            <div className="flex space-x-1">
              {items.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    'h-1 w-1 rounded-full transition-colors',
                    index === activeTab ? 'bg-primary-500' : 'bg-gray-300'
                  )}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="my-2 flex-1 overflow-auto">
        {items[activeTab]?.content}
      </div>
    </div>
  );
}
